import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileJson, FileText, Upload } from 'lucide-react';

import { Button } from '@/components/Common/button';
import organizationImportService from '@/services/organizationImportService';

interface StepUploadProps {
  file: File | null;
  onFileSelected: (file: File) => void;
}

const ACCEPT = '.yaml,.yml,.json,application/json,application/x-yaml,text/yaml';

export const StepUpload: React.FC<StepUploadProps> = ({ file, onFileSelected }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const triggerPicker = () => inputRef.current?.click();

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    onFileSelected(files[0]);
  };

  const downloadExample = async (fmt: 'yaml' | 'json', kind: 'full' | 'minimal') => {
    setDownloadError(null);
    try {
      await organizationImportService.downloadExample(fmt, kind);
    } catch (err) {
      console.error('Failed to download example:', err);
      setDownloadError(
        t(
          'organizations.import.upload.exampleDownloadError',
          "Impossible de télécharger l'exemple",
        ),
      );
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h4 className="text-sm font-semibold mb-1">
          {t('organizations.import.upload.title', 'Choisir un fichier')}
        </h4>
        <p className="text-sm text-muted-foreground">
          {t(
            'organizations.import.upload.description',
            'Importez une organisation et toute sa hiérarchie (domaines, cas d\'usage, cadrages) depuis un fichier YAML ou JSON.',
          )}
        </p>
      </div>

      <div
        onClick={triggerPicker}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        {file ? (
          <div>
            <div className="font-medium text-sm">{file.name}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
        ) : (
          <div>
            <div className="text-sm font-medium">
              {t('organizations.import.upload.dropHere', 'Glissez un fichier ici ou cliquez pour sélectionner')}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('organizations.import.upload.formats', 'Formats : .yaml, .yml, .json')}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-muted/30 p-4 space-y-3">
        <div className="text-sm font-semibold">
          {t('organizations.import.upload.examplesTitle', 'Exemples de fichiers')}
        </div>
        <p className="text-xs text-muted-foreground">
          {t(
            'organizations.import.upload.examplesDescription',
            'Téléchargez un modèle pour partir d\'une structure valide. Le modèle complet inclut domaines, cas d\'usage et cadrages.',
          )}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadExample('yaml', 'full')} className="gap-2 justify-start">
            <FileText className="h-4 w-4" />
            {t('organizations.import.upload.exampleFullYaml', 'Complet · YAML')}
            <Download className="h-3.5 w-3.5 ml-auto" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExample('json', 'full')} className="gap-2 justify-start">
            <FileJson className="h-4 w-4" />
            {t('organizations.import.upload.exampleFullJson', 'Complet · JSON')}
            <Download className="h-3.5 w-3.5 ml-auto" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExample('yaml', 'minimal')} className="gap-2 justify-start">
            <FileText className="h-4 w-4" />
            {t('organizations.import.upload.exampleMinimalYaml', 'Minimal · YAML')}
            <Download className="h-3.5 w-3.5 ml-auto" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadExample('json', 'minimal')} className="gap-2 justify-start">
            <FileJson className="h-4 w-4" />
            {t('organizations.import.upload.exampleMinimalJson', 'Minimal · JSON')}
            <Download className="h-3.5 w-3.5 ml-auto" />
          </Button>
        </div>
        {downloadError && (
          <p className="text-xs text-destructive">{downloadError}</p>
        )}
      </div>
    </div>
  );
};

export default StepUpload;
